import React, { useRef } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { useEffect, useState } from "react"
import Text from "aldoo-ra/Text"
import DynamicSVG from "aldoo-ra/DynamicSVG"
import CMSData from "aldoo-ra/CMS/cms-data"
import Value from "aldoo-ra/Value"
import { api } from "aldoo-ra/API"
import { priceDisplayFormat } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import Localization from "aldoo-ra/Localization"
import { store } from "aldoo-ra/Store"
import ExpressionParser from "aldoo-ra/ExpressionParser"
import Icon from "aldoo-ra/Icon"
import ContentDistancer from "./ContentDistancer"
import PubSub from "pubsub-js"

//import the store product API
const StoreAPI = api("store")

const PlanPriceCard = ({ card }) => {
  const cardType = Array.isArray(card.card_type)
    ? card.card_type[0]
    : card.card_type
  const isFree = cardType === "free"
  const [category] = Value("plan_category")

  const [bundlePrice, setBundlePrice] = useState(card.price)
  const [pricePerYear, setPricePerYear] = useState(0)
  const [isYearly] = Value("plan_duration")

  //Listen to the plan duration switcher
  useEffect(() => {
    if (isFree) return

    setBundlePrice(card.price)
    setPricePerYear(card.price_per_year)

    //update the yearly price display (only for individual cards)
    if (isYearly && card.product?.duration === 365 && card.card_category === "individual") {
      // Calculate the price per year
      let monthly = (card.price / 12).toFixed(2)
      //round up to the nearest .99
      monthly = ((monthly * 100 - 1) / 100).toFixed(2)
      if (isNaN(monthly)) monthly = ""
      setBundlePrice(monthly)
      setPricePerYear(card.price)
    }
  }, [isYearly, category, card.price])

  // Visual-only "old price": struck through next to the real price.
  // Purely cosmetic — pricing/checkout always use card.price / the product.
  // Entered as a plain number in the same currency as the real price, so it is
  // not recomputed when the monthly/yearly switcher rewrites bundlePrice.
  const visualDiscountPrice = parseFloat(card.visual_discount_price)
  const hasVisualDiscount =
    !isFree && Number.isFinite(visualDiscountPrice) && visualDiscountPrice > 0

  //don't show the card if it's not yearly and the plan is yearly
  if (
    card.card_category === "individual" &&
    !isFree &&
    ((isYearly && card.product?.duration !== 365) ||
      (!isYearly && card.product?.duration === 365))
  )
    return null

  const handleButtonClick = () => {
    // If it's the free plan, open the promo banner modal
    if (isFree) {
      PubSub.publish("open-promo-banner")
      return
    }

    // Check if this is a "Contact Us" card (business plan)
    if (card.cta?.toLowerCase().includes("contact")) {
      PubSub.publish("open-contacts", { tag: "business" })
      return
    }

    // GA4 view_item + add_to_cart for paid plan intent. Fire before any
    // navigate_target branching so the funnel records the click even when the
    // CTA opens a custom flow that bypasses store.addToCart.
    const gaItem = {
      item_id: card.product?.product_id || card.product?.item,
      item_name: card.product?.name || card.product?.product_id,
      item_list_name: "Plans",
      price: Number(card.price) || 0,
      quantity: 1,
    }
    const gaCurrency = card.product?.currency
    const gaValue = Number(card.price) || 0
    PubSub.publish("Tracking_ViewItem", {
      currency: gaCurrency,
      value: gaValue,
      items: [gaItem],
    })

    // For other plans, use the existing behavior
    //read the navigate_target card options
    if (
      Array.isArray(card.navigate_target) &&
      card.navigate_target[0].options?.action
    ) {
      // Populate the cart in the background so:
      //   1) Store.addToCart auto-publishes Tracking_AddToCart
      //   2) onPaymentSuccess captures a non-empty cart → Tracking_Purchase
      //      actually fires (it's guarded by receiptCart.length).
      // proceedToCart:false keeps the cart UI hidden — the navigate_target
      // action drives its own UI.
      store.addToCart({
        product: card.product,
        proceedToCart: false,
      })
      // begin_checkout still needs a manual publish — the navigate_target
      // flow doesn't go through Store.beginCheckout.
      PubSub.publish("Tracking_BeginCheckout", {
        currency: gaCurrency,
        value: gaValue,
        items: [gaItem],
      })
      PubSub.publish(card.navigate_target[0].options?.action)
      return
    }

    store.addToCart({
      product: card.product,
      proceedToCart: true,
    })
  }

  return (
    <div className="relative w-[330px] md:w-[400px] xl:w-[434px] h-[400px] md:h-[420px] lg:h-[520px] xl:h-[530px] 2xl:h-[600px] flex flex-col justify-between bg-theme_white rounded-[18px] lg:rounded-[24px] shadow-custom_card pt-10 lg:pt-[50px] 2xl:pt-[76px] pb-5 lg:pb-8 px-6 lg:px-8 xl:px-[56px] overflow-hidden">
      {/* Light-green rectangle in top-right corner of Premium */}
      {card.promo_features && (
        <div className="absolute top-0 right-0 lg:w-full xl:w-auto flex xl:flex-col lg:justify-center xl:justify-start gap-3 xl:gap-0 bg-primary_lt rounded-bl-[18px] lg:rounded-bl-none xl:rounded-bl-[24px] px-4 lg:px-3 py-[5px] lg:py-2">
          {/* Promo Features */}
          {card.promo_features.map((feature, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 text-primary text-sm"
            >
              <img
                src={feature.image}
                alt={feature.title}
                className="w-3 lg:w-4 h-3 lg:h-4 object-cover"
              />

              <Typography variant="info_15" className="text-gray_1 whitespace-nowrap my-0">
                {feature.title}
              </Typography>
            </div>
          ))}
        </div>
      )}

      {/* Card content section */}
      <div className="relative">
        {/* Title */}
        <div className="flex flex-col lg:flex-row lg:items-baseline gap-2 lg:gap-3 mb-2 lg:mb-0 2xl:mb-2">
          <Typography
            component="h2"
            variant="h3_accent"
            className="text-primary"
          >
            {card.title}
          </Typography>

          {card.user_limit && (
            <Typography variant="info_16" className="text-text -mt-2 mb-0">
              {card.user_limit}
            </Typography>
          )}
        </div>

        {/* Platforms */}
        <div className="flex gap-2 mb-5 lg:mb-3 2xl:lg:mb-5">
          {card.platforms.map((platform, index) => (
            <DynamicSVG
              key={index}
              url={platform.image}
              recolor={false}
              className="w-4 h-4 lg:w-5 lg:h-5"
            />
          ))}
        </div>

        {/* Price */}
        <div className="absolute lg:relative flex flex-col items-end lg:items-start top-0 right-0 mb-4 lg:mb-3">
          <Typography
            variant="h4"
            className="text-right lg:text-left leading-none my-0"
          >
            {!isFree && card.price ? (
              <>
                {hasVisualDiscount && (
                  <span className="mr-2 text-[0.55em] font-normal text-gray_1 line-through decoration-gray_1 decoration-[0.1em]">
                    {priceDisplayFormat(
                      visualDiscountPrice,
                      card.product?.currency
                    )}
                  </span>
                )}
                {priceDisplayFormat(bundlePrice, card.product?.currency)}
                <span className="text-[18px] lg:text-[24px] text-right lg:text-left font-light text-text my-0">
                  {card.duration}
                </span>
              </>
            ) : (
              priceDisplayFormat(card.price, card.product?.currency)
            )}
          </Typography>

          <div className="h-[26px] lg:-mt-1">
            {!isFree && pricePerYear && (
              <Text
                id="billed_per_year"
                data={{
                  price: priceDisplayFormat(
                    pricePerYear,
                    card.product?.currency
                  ),
                }}
                variant="info_16"
                className="text-text my-0"
              />
            )}
          </div>
        </div>

        {/* Accounts and Ad options */}
        <div className="space-y-1 mb-4 lg:mb-3 2xl:mb-4">
          {card.features.map((feature, index) => (
            <div key={index} className="flex items-center gap-3">
              <img
                src={feature.image}
                alt={feature.title}
                className="w-[18px] lg:w-6 h-[18px] lg:h-6"
              />
              <Typography
                variant="info_18_light_minor"
                className="text-text my-0"
              >
                {feature.title}
              </Typography>
            </div>
          ))}
        </div>

        {/* Access and features */}
        <div>
          <Typography
            variant="body_20_accent_major"
            className="text-text mt-0 !mb-2 lg:!mb-2 2xl:!mb-4"
          >
            {card.access}:
          </Typography>

          <div className="space-y-1">
            {card.accessFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                {feature.enabled ? (
                  <Icon
                    id="feature_check_mark"
                    className="w-4 lg:w-6 h-4 lg:h-6"
                  />
                ) : (
                  <Icon id="feature_x_mark" className="w-4 lg:w-6 h-4 lg:h-6" />
                )}

                <Typography
                  variant="info_18_light_minor"
                  className="text-text my-0"
                >
                  {feature.name}
                </Typography>
              </div>
            ))}
          </div>
        </div>

        {/* Footer notes */}
        <div className="text-center lg:mt-5">
          <Typography
            variant="body_20_accent_major"
            className="text-left text-text mb-4 lg:mb-0"
          >
            {card.footer_notes}
          </Typography>
        </div>
      </div>

      <Button
        variant={`${isFree ? "secondary_m" : "primary_m"}`}
        className="mx-auto"
        onClick={handleButtonClick}
      >
        {card.cta}
      </Button>
    </div>
  )
}

const PricingCards = ({ card_category }) => {
  const [cards, setCards] = useState([])
  //to be visible in the duration switcher
  const [, setPlanCards] = Value("plan_cards")
  const { lt, t } = Localization()

  //base query
  const query = {
    contentType: "PlanPriceCard",
  }

  //add card category to the query if it specified
  if (card_category) query.match = { "content.card_category": card_category }

  //get the cards from the server
  const { data } = CMSData(query)
  const prevData = useRef("")
  const viewItemListFiredRef = useRef(false)

  // GA4 view_item_list — fires once when plans + prices have resolved so items
  // carry real id/name/price. Without this, GA4 treats add_to_cart as funnel
  // entry and drop-off attribution is skewed.
  useEffect(() => {
    if (viewItemListFiredRef.current) return
    if (!cards?.length) return
    const items = cards
      .filter((c) => c.product?.product_id || c.product?.item)
      .map((c) => ({
        item_id: c.product?.product_id || c.product?.item,
        item_name: c.product?.name || c.product?.product_id,
        item_list_name: "Plans",
        price: Number(c.price) || 0,
        quantity: 1,
      }))
    if (!items.length) return
    const currency = cards.find((c) => c.product?.currency)?.product?.currency
    // Defer one tick so GoogleTracking (parent useEffect) has subscribed.
    // React fires child effects before parent's — without this, the publish
    // happens before the subscriber registers and the event is dropped.
    setTimeout(() => {
      PubSub.publish("Tracking_ViewItemList", {
        items,
        currency,
        itemListName: "Plans",
      })
    }, 0)
    viewItemListFiredRef.current = true
  }, [cards])

  //get the products for the cards
  useEffect(() => {
    if (!data || !data.length) return

    //if the products are already fetched, return
    const deserialziedData = JSON.stringify(data)
    if (deserialziedData === prevData.current) return
    prevData.current = deserialziedData

    //get the products for the cards
    ~(async () => {
      const productIDs = data.map((card) => card.product?.item).filter(Boolean)

      //get the products prices via the store API
      const result = await StoreAPI({
        resolve_price: {
          products: productIDs,
          platform: "Web",
        },
      })

      //the result is an array or objects { product_id, amount, currency, store_id}
      //map to the cards
      data.map(async (card) => {
        const product = result.find(
          (product) => product.product_id === card.product?.item
        )
        if (!product) return

        card.user_limit =
          ExpressionParser.evaluate(card.user_limit, { product })


        //rewrite the price
        if (product.amount) {
          card.price = ExpressionParser.evaluate(card.price, { product })
          //round up to the nearest .99
          if (card.price && !card.price.toString().includes(".99"))
            card.price = ((card.price * 100 - 1) / 100).toFixed(2)
          else if (!card.price) card.price = 0
        } else {
          card.price = 0
        }

        card.product = product
        //expand price per year
        card.price_per_year = t(card.price_per_year, { product })

        //localize the product name
        product.name && (product.name = await lt(product.name))
        //get the product description
        product.description &&
          (product.description = await lt(product.description))
      })

      //update the cards
      setCards(data)
      if (card_category === "individual") setPlanCards(data)
    })()
  }, [data])

  return (
    <div className="flex justify-center items-start mx-auto">
      <div className="flex flex-col items-center gap-5 pt-[88px] xl:pt-0 md:mb-[200px]">
        <div className="flex flex-col lg:flex-row w-full lg:w-[988px] items-center lg:items-start justify-center gap-6 mx-auto">
          {cards
            .filter((item) => {
              // Filter by card category
              const categoryMatch =
                item.card_category === card_category ||
                (Array.isArray(item.card_category) &&
                  item.card_category[0] === card_category)

              // Filter out products that are not in stock or not available
              const isAvailable = !item.product ||
                (item.product.in_stock !== false && item.product.available !== false)

              return categoryMatch && isAvailable
            })
            .map((card, index) => (
              <PlanPriceCard key={index} card={card} />
            ))}
        </div>
      </div>

      {/* Invisible div for distancing the side menu from the cards */}
      <ContentDistancer height="500px" />
    </div>
  )
}

const AldooComponent = {
  id: 'PlanPriceCards',
  component: PricingCards,
  input: {},
  meta: {
    name: 'Plan Price Cards',
    description: 'Subscription plan pricing cards with category filtering',
    category: 'Marketing',
  },
}

export { AldooComponent }
export default PricingCards
